class CodeltyCommerceCta {
  static SUCCESS_MS = 2000;
  static ERROR_MS = 2600;

  static init(root = document) {
    root.querySelectorAll('.codelty-commerce-cta:not([data-codelty-cta-bound])').forEach((button) => {
      button.dataset.codeltyCtaBound = 'true';
      CodeltyCommerceCta.storeDefaultLabel(button);
      button.addEventListener('pointerdown', (event) => CodeltyCommerceCta.ripple(event, button));
    });

    if (!CodeltyCommerceCta._eventsBound && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      CodeltyCommerceCta._eventsBound = true;

      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'product-form') return;
        CodeltyCommerceCta.bumpCartIcon();
      });
    }
  }

  static strings() {
    return window.commerceCtaStrings || window.variantStrings || {};
  }

  static labelElement(button) {
    return (
      button.querySelector('[data-codelty-cta-label]') ||
      button.querySelector('.codelty-commerce-cta__label')
    );
  }

  static storeDefaultLabel(button) {
    const label = CodeltyCommerceCta.labelElement(button);
    if (label && !button.dataset.codeltyCtaDefaultLabel) {
      button.dataset.codeltyCtaDefaultLabel = label.textContent.trim();
    }
  }

  static defaultLabel(button) {
    return button.dataset.codeltyCtaDefaultLabel || CodeltyCommerceCta.strings().addToCart || '';
  }

  static successLabel(button) {
    return (
      button.dataset.successLabel ||
      button.dataset.addedLabel ||
      CodeltyCommerceCta.strings().success ||
      CodeltyCommerceCta.strings().addedToCart ||
      'Added'
    );
  }

  static errorLabel(button) {
    return button.dataset.errorLabel || CodeltyCommerceCta.strings().error || 'Error';
  }

  static setLabel(button, text) {
    const label = CodeltyCommerceCta.labelElement(button);
    if (label) label.textContent = text;
  }

  static spinner(button) {
    return button.querySelector('.loading__spinner');
  }

  static setLoading(button, loading = true) {
    if (!button) return;

    if (loading) {
      button.classList.remove('is-success', 'is-added', 'is-error');
    }

    window.requestAnimationFrame(() => {
      button.classList.toggle('loading', loading);
      button.toggleAttribute('aria-busy', loading);

      const spinner = CodeltyCommerceCta.spinner(button);
      if (spinner) spinner.classList.toggle('hidden', !loading);

      CodeltyCommerceCta.syncMirrors(button, loading ? 'loading' : 'idle');
    });
  }

  static setSuccess(button, options = {}) {
    if (!button) return;

    const duration = options.duration ?? CodeltyCommerceCta.SUCCESS_MS;

    button.classList.remove('loading', 'is-error');
    button.removeAttribute('aria-busy');

    const spinner = CodeltyCommerceCta.spinner(button);
    if (spinner) spinner.classList.add('hidden');

    window.requestAnimationFrame(() => {
      button.classList.add('is-success', 'is-added');
      CodeltyCommerceCta.setLabel(button, options.label || CodeltyCommerceCta.successLabel(button));
      CodeltyCommerceCta.syncMirrors(button, 'success', options);
    });

    window.clearTimeout(button._codeltyCtaResetTimer);
    button._codeltyCtaResetTimer = window.setTimeout(() => {
      CodeltyCommerceCta.reset(button);
    }, duration);
  }

  static setError(button, options = {}) {
    if (!button) return;

    const duration = options.duration ?? CodeltyCommerceCta.ERROR_MS;

    button.classList.remove('loading', 'is-success', 'is-added');
    button.classList.add('is-error');
    button.removeAttribute('aria-busy');

    const spinner = CodeltyCommerceCta.spinner(button);
    if (spinner) spinner.classList.add('hidden');

    CodeltyCommerceCta.setLabel(button, options.label || CodeltyCommerceCta.errorLabel(button));
    CodeltyCommerceCta.syncMirrors(button, 'error', options);

    window.clearTimeout(button._codeltyCtaResetTimer);
    button._codeltyCtaResetTimer = window.setTimeout(() => {
      CodeltyCommerceCta.reset(button);
    }, duration);
  }

  static reset(button) {
    if (!button) return;

    button.classList.remove('is-success', 'is-added', 'is-error', 'loading');
    button.removeAttribute('aria-busy');

    const spinner = CodeltyCommerceCta.spinner(button);
    if (spinner) spinner.classList.add('hidden');

    CodeltyCommerceCta.setLabel(button, CodeltyCommerceCta.defaultLabel(button));
    CodeltyCommerceCta.syncMirrors(button, 'reset');
  }

  static registerMirror(mirrorButton, sourceButton) {
    if (!mirrorButton || !sourceButton) return;
    sourceButton._codeltyCtaMirrors = sourceButton._codeltyCtaMirrors || [];
    if (!sourceButton._codeltyCtaMirrors.includes(mirrorButton)) {
      sourceButton._codeltyCtaMirrors.push(mirrorButton);
    }
    CodeltyCommerceCta.storeDefaultLabel(mirrorButton);
  }

  static syncMirrors(sourceButton, state, options = {}) {
    const mirrors = sourceButton?._codeltyCtaMirrors || [];
    mirrors.forEach((mirror) => {
      if (state === 'loading') CodeltyCommerceCta.setLoading(mirror, true);
      if (state === 'idle') CodeltyCommerceCta.setLoading(mirror, false);
      if (state === 'success') CodeltyCommerceCta.setSuccess(mirror, options);
      if (state === 'error') CodeltyCommerceCta.setError(mirror, options);
      if (state === 'reset') CodeltyCommerceCta.reset(mirror);
    });
  }

  static ripple(event, button) {
    if (button.disabled || button.classList.contains('loading')) return;

    const ripple = document.createElement('span');
    ripple.className = 'codelty-commerce-cta__ripple';
    ripple.setAttribute('aria-hidden', 'true');

    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${event.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${event.clientY - rect.top - size / 2}px`;

    button.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
  }

  static bumpCartIcon() {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon) return;
    cartIcon.classList.remove('codelty-cart-bump');
    window.requestAnimationFrame(() => cartIcon.classList.add('codelty-cart-bump'));
    window.setTimeout(() => cartIcon.classList.remove('codelty-cart-bump'), 700);
  }

  static observe(root = document.body) {
    if (CodeltyCommerceCta._observer || !window.MutationObserver) return;

    CodeltyCommerceCta._observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.('.codelty-commerce-cta')) CodeltyCommerceCta.init(node.parentElement || root);
          else CodeltyCommerceCta.init(node);
        });
      });
    });

    CodeltyCommerceCta._observer.observe(root, { childList: true, subtree: true });
  }
}

window.CodeltyCommerceCta = CodeltyCommerceCta;

const bootCommerceCta = () => {
  CodeltyCommerceCta.init();
  CodeltyCommerceCta.observe();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootCommerceCta);
} else {
  bootCommerceCta();
}
